import axios from 'axios';

// ─── Resolve the backend base URL ────────────────────────────────────────────
// Priority:
//  1. REACT_APP_API_URL env var  (always set for production builds / native apps)
//  2. Same origin (works for web where React app is served by the Express server)
//
// In a Capacitor native app, window.location.origin is "capacitor://localhost"
// or "https://localhost" — neither reaches our backend.  So REACT_APP_API_URL
// MUST be set in the .env before building for Android / iOS.
const isCapacitorNative = () =>
  !!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform());

const resolveBaseURL = () => {
  // Explicit env var always wins (required for native builds)
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  // In-browser web: use the same origin so CRA proxy works in dev
  if (!isCapacitorNative()) return window.location.origin;
  // Safety fallback — should not reach here in production native builds
  console.warn('[axios] REACT_APP_API_URL is not set. API calls may fail in native app.');
  return '';
};

axios.defaults.baseURL = resolveBaseURL();

// Setup axios request interceptor to add auth token and campaignId
axios.interceptors.request.use(
  config => {
    // Add admin or agent token to Authorization header
    const adminToken = localStorage.getItem('adminToken');
    const agentToken = localStorage.getItem('agentToken');
    
    if (adminToken && config.url && config.url.includes('/admin')) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (agentToken && config.url && (config.url.includes('/agent') || config.url.includes('/2fa'))) {
      config.headers.Authorization = `Bearer ${agentToken}`;
    }
    
    // Add campaignId for admin, agent, and 2fa API calls
    const isAdminRequest = config.url && config.url.includes('/admin');
    const isAgentRequest = config.url && config.url.includes('/agent');
    const is2FARequest  = config.url && config.url.includes('/2fa');
    const isCampaignsListRequest = config.url && config.url.includes('/admin/campaigns') && !config.url.includes('/admin/campaigns/');
    
    if ((isAdminRequest && !isCampaignsListRequest) || isAgentRequest || is2FARequest) {
      const campaignId = localStorage.getItem('selectedCampaign') || 'medicare';
      
      // Add campaignId as header (backend checks this)
      config.headers['X-Campaign-ID'] = campaignId;
      
      console.log('[Axios] Added campaignId to request:', campaignId, 'URL:', config.url);
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Setup axios response interceptor for handling 401/403 errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Don't redirect if we're on 2FA setup/verify pages
      const is2FAPage = window.location.pathname.includes('/setup-2fa') || 
                        error.config?.url?.includes('/2fa/');
      
      if (is2FAPage) {
        return Promise.reject(error);
      }
      
      const isAdmin = window.location.pathname.includes('/admin');
      const isAgent = window.location.pathname.includes('/agent');
      
      if (isAdmin) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      } else if (isAgent) {
        localStorage.removeItem('agentToken');
        window.location.href = '/agent/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
